import React from 'react';
import { Box, Flex, Icon, Tab, TabProps } from 'pounceV2';
import { FastField, useField, useFormikContext } from 'formik';
import { FormikTextInputV3 } from 'Components/fields/TextInput/TextInputV3';
import OverflowTextWithTooltipV3 from 'Components/OverflowTextWithTooltip/OverflowTextWithTooltipV3';
import { useTheme } from '@mui/material';
import EditMenuV3 from './EditMenuV3';

type RenamableTabProps = {
  name: string;
  onDelete: React.MouseEventHandler<HTMLButtonElement>;
  onEditOverride?: () => void;
  canEdit?: boolean;
  isPantherManaged?: boolean;
  component?: React.ElementType;
  startEditing?: boolean;
  /**
   * For validation purposes, can be list of other tab names if names need to be unique
   */
  uniqueCheck?: string[];
  errorMessage?: string;
  /**
   * These props are automatically passed by `TabList` and not by the developer.
   */
  /** Whether the tab is selected */
  isSelected?: boolean;
  /** Whether the tab is focused */
  isFocused?: boolean;
  /** If true, validation will only be performed on the field this tab represents, not entire form */
  validateLocalOnly?: boolean;
  /** If true, draw the Rename/Delete buttons with a corresponding icon */
  useIcons?: boolean;
} & Partial<TabProps>;

const RenamableTabV3: React.FC<RenamableTabProps> = ({
  component: Component = Tab,
  name = '',
  onDelete,
  onEditOverride,
  canEdit = true,
  isPantherManaged = false,
  uniqueCheck = [],
  errorMessage = 'Cannot have duplicate tab name',
  isSelected,
  isFocused,
  validateLocalOnly,
  useIcons = false,
  ...rest
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const [editing, setEditing] = React.useState(false);
  const [originalTabName, setOriginalTabName] = React.useState('');
  const [field, , helpers] = useField(name);
  const { validateForm } = useFormikContext();

  const ref = React.useRef(null);

  // handles exiting edit mode by clicking outside of the edit box without blur (hitting Rename without clicking inside the box)
  React.useEffect(() => {
    const handleClickOutside = event => {
      // Only handle left-click (button 0). Right-click and middle-click should not exit edit mode.
      if (event.button !== 0) {
        return;
      }
      if (ref.current && !ref.current.contains(event.target)) {
        if (!validateLocalOnly) {
          validateForm();
        }
        // Reset to original name if empty
        if (field.value.trim() === '') {
          helpers.setValue(originalTabName);
        }
        if (uniqueCheck.filter(testName => testName === field.value).length <= 1) {
          setEditing(false);
        } else if (validateLocalOnly) {
          helpers.setError(errorMessage);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [
    field.value,
    helpers,
    errorMessage,
    uniqueCheck,
    validateForm,
    validateLocalOnly,
    originalTabName,
  ]);

  const handleLabelKeypress = React.useCallback(
    event => {
      if (event.key === 'Enter') {
        if (!validateLocalOnly) {
          validateForm();
        }
        helpers.setTouched(true);
        // Reset to original name if empty
        if (field.value.trim() === '') {
          helpers.setValue(originalTabName);
        }
        if (uniqueCheck.filter(testName => testName === field.value).length <= 1) {
          setEditing(false);
        } else if (validateLocalOnly) {
          helpers.setError(errorMessage);
        }
      } else if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        // prevents arrow keys from moving between tabs
        event.stopPropagation();
      }
    },
    [
      field.value,
      helpers,
      errorMessage,
      uniqueCheck,
      validateForm,
      validateLocalOnly,
      originalTabName,
    ],
  );

  const validateUniqueness = currentName => {
    if (uniqueCheck.filter(testName => testName === currentName).length > 1) {
      return errorMessage;
    }
    return null;
  };

  return (
    <Component
      // handles nested button error
      as={Box}
      cursor="pointer"
      variant="compact"
      isSelected={isSelected}
      isFocused={isFocused}
      {...rest}
    >
      <Flex align="center" justify="space-between" gap={2}>
        {isPantherManaged || !canEdit ? (
          <>
            {isPantherManaged && (
              <Icon type="panther" size="medium" color={tokens.content.assets.accent[14][2]} />
            )}
            <OverflowTextWithTooltipV3>{field.value}</OverflowTextWithTooltipV3>
          </>
        ) : (
          <>
            {editing ? (
              <Box minWidth="300px" ref={ref}>
                <FastField
                  as={FormikTextInputV3}
                  name={field.name}
                  label="Rename Tab"
                  onKeyDown={handleLabelKeypress}
                  validate={validateUniqueness}
                  autoFocus
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                />
              </Box>
            ) : (
              <OverflowTextWithTooltipV3>{field.value}</OverflowTextWithTooltipV3>
            )}
            {!editing && (
              <EditMenuV3
                useIcons={useIcons}
                onEdit={() => {
                  if (onEditOverride) {
                    onEditOverride();
                  } else {
                    setOriginalTabName(field.value);
                    setEditing(true);
                  }
                }}
                onDelete={onDelete}
              />
            )}
          </>
        )}
      </Flex>
    </Component>
  );
};

export default React.memo(RenamableTabV3);
